import Reviews from "@mui/icons-material/Reviews";
import {
  RecommendationTypeHandler,
  RecommendationAdapter,
  NormalizedRecommendation,
  ListItemContent,
} from "../types";
import AssessmentRecommendationDetailsDialog from "../../components/assessment/AssessmentRecommendationDetailsDialog";
import {
  ResponseRecommendation,
  RoadmapItemSourceType,
  AssessmentDto,
} from "../../../../../../customer-experience/src/lib/ShiOneClient";
import { DateTime } from "luxon";
import { roadmapHelpers } from "../../utils/roadmapHelpers";

const assessmentAdapter: RecommendationAdapter<ResponseRecommendation> = {
  normalize: (rec: ResponseRecommendation): NormalizedRecommendation => {
    const searchableFields = [
      rec.category,
      rec.capability,
      rec.recommendationsSummary,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return {
      id: rec.recommendationId || "",
      sourceId: `${rec.assessmentId}:${rec.recommendationId}`,
      sourceType: "assessment",
      title: rec.capability || "",
      description: rec.recommendationsSummary || "",
      category: rec.category || "Uncategorized",
      searchableText: searchableFields,
      metadata: {
        assessmentId: rec.assessmentId,
      },
      originalData: rec,
      isLinkedToRoadmap: rec.isLinkedToRoadmap || false,
    };
  },

  getSourceId: (rec: ResponseRecommendation) =>
    `${rec.assessmentId}:${rec.recommendationId}`,

  getTitle: (rec: ResponseRecommendation) => rec.capability || "",

  getDescription: (rec: ResponseRecommendation) =>
    rec.recommendationsSummary || "",
};

export const assessmentRecommendationHandler: RecommendationTypeHandler<ResponseRecommendation> =
  {
    sourceType: RoadmapItemSourceType.AssessmentRecommendation,

    displayConfig: {
      icon: Reviews,
      iconColor: "text.secondary",
      label: "Assessment Recommendation",
      showDescription: true,
    },

    adapter: assessmentAdapter,

    detailDialog: AssessmentRecommendationDetailsDialog,

    createMetadata: (recommendation: ResponseRecommendation) => {
      return {
        recommendationId: recommendation.recommendationId,
        assessmentId: recommendation.assessmentId,
        capability: recommendation.capability,
        impact: recommendation.impact,
        category: recommendation.category,
        subcategory: recommendation.subcategory,
        program: recommendation.program,
      };
    },

    isType: (recommendation: any): recommendation is ResponseRecommendation => {
      // Check if it's a ResponseRecommendation object or has the required properties
      return (
        recommendation?.constructor?.name === "ResponseRecommendation" ||
        ("assessmentId" in recommendation &&
          "recommendationId" in recommendation) ||
        ("assessmentId" in recommendation && "capability" in recommendation)
      );
    },

    sourceSection: {
      sectionLabel: "ASSESSMENTS",
      getSources: (assessments: AssessmentDto[]) => {
        const latestByProgram = assessments.reduce(
          (acc, assessment) => {
            const program = assessment.program || "Unknown";
            if (
              !acc[program] ||
              new Date(assessment.assessmentDate!).getTime() >
                new Date(acc[program].assessmentDate!).getTime()
            ) {
              acc[program] = assessment;
            }
            return acc;
          },
          {} as Record<string, AssessmentDto>,
        );

        return Object.values(latestByProgram)
          .sort(
            (a, b) =>
              new Date(b.assessmentDate!).getTime() -
              new Date(a.assessmentDate!).getTime(),
          )
          .map((assessment) => ({
            id: assessment.id || "",
            label: assessment.program || "Unknown Assessment",
            secondaryLabel: DateTime.fromJSDate(
              assessment.assessmentDate as Date,
            ).toFormat("LLL dd, yyyy"),
          }));
      },
    },

    getListItemContent: (
      recommendation: ResponseRecommendation,
    ): ListItemContent => ({
      primary: recommendation.capability || "",
      secondary: roadmapHelpers(
        recommendation.recommendationsSummary || "",
        120,
      ),
    }),

    getChipLabel: (recommendation: ResponseRecommendation): string => {
      return recommendation.capability || "";
    },

    getGroupKey: (recommendation: ResponseRecommendation): string => {
      return recommendation.assessmentId || "unknown";
    },

    getGroupTitle: (
      groupKey: string,
      context?: { assessments?: any[] },
    ): string => {
      if (!context?.assessments) return groupKey;

      const assessment = context.assessments.find(
        (a: any) => a.id === groupKey,
      );
      if (assessment) {
        return assessment.program || "Unknown Assessment";
      }

      return groupKey;
    },
  };
